import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useStrings } from '@/hooks/useStrings';
import ScreenHeader from '@/components/ui/ScreenHeader';

// ── Styles ───────────────────────────────────────────────────────────────

const s = {
  screen: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'var(--cream, #FAF6F1)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexShrink: 0,
    padding: 'calc(env(safe-area-inset-top, 0px) + 16px) 20px 12px',
    borderBottom: '1px solid var(--beige, #E8DDD0)',
    background: 'var(--cream, #FAF6F1)',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px 8px 0',
    background: 'transparent',
    border: 'none',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--warm-brown, #8B7355)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '24px 24px calc(40px + env(safe-area-inset-bottom, 0px))',
  },
  container: {
    width: '100%',
    maxWidth: 640,
    margin: '0 auto',
  },
  draftBanner: {
    background: '#FEF2F2',
    border: '1.5px solid #FCA5A5',
    borderRadius: 'var(--radius-md, 12px)',
    padding: '14px 16px',
    marginBottom: 24,
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 13,
    fontWeight: 600,
    color: '#991B1B',
    lineHeight: 1.5,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  lastUpdated: {
    fontFamily: 'var(--font-ui)',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: 'var(--warm-brown)',
    margin: '0 0 28px',
  },
  intro: {
    fontFamily: 'var(--font-body)',
    fontSize: 18,
    fontWeight: 400,
    color: 'var(--text)',
    lineHeight: 1.7,
    margin: '0 0 36px',
  },
  sectionTitle: {
    fontFamily: 'var(--font-ui)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--primary)',
    margin: '0 0 12px',
  },
  sectionBody: {
    fontFamily: 'var(--font-body)',
    fontSize: 17,
    fontWeight: 400,
    color: 'var(--text)',
    lineHeight: 1.7,
    margin: '0 0 28px',
  },
};

function Section({ titleKey, bodyKey, t }) {
  return (
    <>
      <h2 style={s.sectionTitle}>{t(titleKey)}</h2>
      <p style={s.sectionBody}>{t(bodyKey)}</p>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export default function PrivacyPolicy() {
  const t = useStrings();
  const navigate = useNavigate();

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/profile');
    }
  }

  return (
    <div style={s.screen}>
      <div style={s.header}>
        <button type="button" style={s.backButton} onClick={handleBack}>
          <span aria-hidden="true">{'\u2190'}</span>
          {t('legal_back_cta')}
        </button>
      </div>

      <div style={s.scroll}>
        <div style={s.container}>
          <div style={{ marginBottom: 18 }}>
            <ScreenHeader
              eyebrow={t('legal_eyebrow') || 'LEGAL'}
              title={t('privacy_title')}
            />
          </div>
          <p style={s.lastUpdated}>
            {t('legal_last_updated')} {'\u00B7'} {t('privacy_date')}
          </p>

          <p style={s.intro}>{t('privacy_intro')}</p>

          <Section titleKey="privacy_what_title" bodyKey="privacy_what_body" t={t} />
          <Section titleKey="privacy_use_title" bodyKey="privacy_use_body" t={t} />
          <Section titleKey="privacy_rights_title" bodyKey="privacy_rights_body" t={t} />
          <Section titleKey="privacy_retention_title" bodyKey="privacy_retention_body" t={t} />
          <Section titleKey="privacy_children_title" bodyKey="privacy_children_body" t={t} />
          <Section titleKey="privacy_contact_title" bodyKey="privacy_contact_body" t={t} />
        </div>
      </div>
    </div>
  );
}
